import { Truck } from '@numeo/types-core'
import { useEffect, useMemo } from 'react'
import { shallowEqual } from 'react-redux'
import { useAppDispatch, useAppSelector } from '@/hooks'
import type { RootState } from '@/store'
import { fleetTrucksApi } from '@/store/api/fleetTrucksApi'
import { selectTeamApplicationId } from '../team/useTeamDirectoryEnabled'

interface TrucksByIdsState {
    /** The trucks found, by id (deleted ones too). An id the company has no truck for is absent. */
    trucks: ReadonlyMap<string, Truck>
    /** Some of the ids are still being asked for. */
    isLoading: boolean
    /** A request for some of the ids failed: those are absent though a truck may exist. */
    isError: boolean
}

const EMPTY: ReadonlyMap<string, Truck> = new Map()

/**
 * The trucks a screen points at (a load's truck, a driver's truck, a row's unit): each id is cached on its own for five
 * minutes and every id asked in the same tick goes in one `GET /fleet/trucks/v1?ids=` request; the pages a screen reads
 * fill the same cache. Deleted trucks are answered too, by name.
 */
export const useTrucksByIds = (ids: readonly (string | null | undefined)[]): TrucksByIdsState => {
    const dispatch = useAppDispatch()
    const applicationId = useAppSelector(selectTeamApplicationId)
    const key = [...new Set(ids.filter((id): id is string => !!id).map(String))].sort().join(',')
    const wanted = useMemo(() => (key ? key.split(',') : []), [key])

    useEffect(() => {
        if (!applicationId || !wanted.length) return
        const subscriptions = wanted.map((id) => dispatch(fleetTrucksApi.endpoints.getTruck.initiate({ applicationId, id })))
        return () => subscriptions.forEach((subscription) => subscription.unsubscribe())
    }, [dispatch, applicationId, wanted])

    const selectors = useMemo(
        () => (applicationId ? wanted.map((id) => fleetTrucksApi.endpoints.getTruck.select({ applicationId, id })) : []),
        [applicationId, wanted]
    )
    const found = useAppSelector((state: RootState) => selectors.map((select) => select(state).data), shallowEqual)
    const loading = useAppSelector((state: RootState) => selectors.some((select) => select(state).isLoading || select(state).isUninitialized))
    const failed = useAppSelector((state: RootState) => selectors.some((select) => select(state).isError))

    return useMemo((): TrucksByIdsState => {
        if (!wanted.length) return { trucks: EMPTY, isLoading: false, isError: false }
        const trucks = new Map<string, Truck>()
        wanted.forEach((id, index) => {
            const truck = found[index]
            if (truck) trucks.set(id, truck)
        })
        return { trucks, isLoading: loading, isError: failed }
    }, [wanted, found, loading, failed])
}
